import Phaser from 'phaser';
import { TEX } from '@/data/assetManifest';
import { T } from '@/data/tileIndex';

// Unified renderer that paints a tile-grid using real 16x16 spritesheets, scaled x2 → 32px world tile.
// Each tile becomes a Phaser.Image at depth 0 (ground) or layer-specific depth.
//
// Usage:
//   const r = new TilemapRenderer(scene, mapW, mapH, 32);
//   r.fill(T.grassGreen);
//   r.rect(28, 28, 52, 52, T.pathCobble);
//   r.placeThing(40, 40, TH.fireplace1);

export type TileLayer = 'ground' | 'deco' | 'object' | 'overhead';

const LAYER_DEPTH: Record<TileLayer, number> = {
  ground: 0,
  deco: 1,
  object: 2,
  overhead: 100000,
};

export class TilemapRenderer {
  private scene: Phaser.Scene;
  private mapW: number;
  private mapH: number;
  private tileSize: number;
  private grid: number[][]; // base ground tile index per (y,x)
  private decoGrid: (number | null)[][];
  private objects: Phaser.GameObjects.GameObject[] = [];

  constructor(scene: Phaser.Scene, mapW: number, mapH: number, tileSize = 32) {
    this.scene = scene;
    this.mapW = mapW;
    this.mapH = mapH;
    this.tileSize = tileSize;
    this.grid = Array.from({ length: mapH }, () => new Array<number>(mapW).fill(T.grassGreen));
    this.decoGrid = Array.from({ length: mapH }, () => new Array<number | null>(mapW).fill(null));
  }

  fill(tileIndex: number) {
    for (let y = 0; y < this.mapH; y++) {
      for (let x = 0; x < this.mapW; x++) this.grid[y][x] = tileIndex;
    }
  }

  rect(x1: number, y1: number, x2: number, y2: number, tileIndex: number) {
    for (let y = y1; y <= y2 && y < this.mapH; y++) {
      for (let x = x1; x <= x2 && x < this.mapW; x++) {
        if (x >= 0 && y >= 0) this.grid[y][x] = tileIndex;
      }
    }
  }

  hLine(x1: number, x2: number, y: number, tileIndex: number) {
    for (let x = x1; x <= x2 && x < this.mapW; x++) {
      if (x >= 0 && y >= 0 && y < this.mapH) this.grid[y][x] = tileIndex;
    }
  }

  vLine(x: number, y1: number, y2: number, tileIndex: number) {
    for (let y = y1; y <= y2 && y < this.mapH; y++) {
      if (y >= 0 && x >= 0 && x < this.mapW) this.grid[y][x] = tileIndex;
    }
  }

  setTile(x: number, y: number, tileIndex: number) {
    if (x >= 0 && y >= 0 && x < this.mapW && y < this.mapH) this.grid[y][x] = tileIndex;
  }

  setDeco(x: number, y: number, tileIndex: number | null) {
    if (x >= 0 && y >= 0 && x < this.mapW && y < this.mapH) this.decoGrid[y][x] = tileIndex;
  }

  // Scatter-deco: randomly sprinkle a tile across the entire map (no overwrite of existing deco).
  scatter(tileIndex: number, count: number, rng: () => number = Math.random) {
    let placed = 0, tries = 0;
    while (placed < count && tries < count * 10) {
      tries++;
      const x = Math.floor(rng() * this.mapW);
      const y = Math.floor(rng() * this.mapH);
      if (this.decoGrid[y][x] == null) {
        this.decoGrid[y][x] = tileIndex;
        placed++;
      }
    }
  }

  // Place a "thing" (16x16 from tiny16_things.png) as a free-floating object
  placeThing(tileX: number, tileY: number, thingIndex: number, layer: TileLayer = 'object'): Phaser.GameObjects.Image {
    const px = tileX * this.tileSize + this.tileSize / 2;
    const py = tileY * this.tileSize + this.tileSize / 2;
    const img = this.scene.add.image(px, py, TEX.tinyThings, thingIndex)
      .setScale(this.tileSize / 16);
    img.setDepth(layer === 'overhead' ? LAYER_DEPTH.overhead : py);
    this.objects.push(img);
    return img;
  }

  // Place a "tile" (16x16 from tiny16_basictiles.png) as object (chair, sign, tree, etc.)
  placeObject(tileX: number, tileY: number, tileIndex: number, layer: TileLayer = 'object', spanW = 1, spanH = 1): Phaser.GameObjects.Image {
    const px = tileX * this.tileSize + (spanW * this.tileSize) / 2;
    const py = tileY * this.tileSize + (spanH * this.tileSize) / 2;
    const img = this.scene.add.image(px, py, TEX.tinyTiles, tileIndex)
      .setScale((this.tileSize * spanW) / 16, (this.tileSize * spanH) / 16);
    img.setDepth(layer === 'overhead' ? LAYER_DEPTH.overhead : py);
    this.objects.push(img);
    return img;
  }

  // Expose grid data for export to MapData format.
  getGround(): number[][] { return this.grid; }
  getDeco(): (number | null)[][] { return this.decoGrid; }

  // Render the ground + deco grids as static images. Call once after all paint ops are done.
  render(): void {
    const scale = this.tileSize / 16;
    for (let y = 0; y < this.mapH; y++) {
      for (let x = 0; x < this.mapW; x++) {
        const idx = this.grid[y][x];
        const px = x * this.tileSize + this.tileSize / 2;
        const py = y * this.tileSize + this.tileSize / 2;
        this.scene.add.image(px, py, TEX.tinyTiles, idx).setScale(scale).setDepth(LAYER_DEPTH.ground);
        const deco = this.decoGrid[y][x];
        if (deco != null) {
          this.scene.add.image(px, py, TEX.tinyTiles, deco).setScale(scale).setDepth(LAYER_DEPTH.deco);
        }
      }
    }
  }
}
